import { findIndex } from "../helpers";
/*
    %d	day as a number with leading zero, 01..31
    %j	day as a number, 1..31
    %D	short name of the day, Su Mo Tu...
    %l	full name of the day, Sunday Monday Tuesday...
    %m	month as a number with leading zero, 01..12
    %n	month as a number, 1..12
    %M	short name of the month, Jan Feb Mar...
    %F	full name of the month, January February March...
    %y	year as a number, 2 digits
    %Y	year as a number, 4 digits
*/
let locales = {
    monthFull: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ],
    monthShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ],
    dayFull: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
    ],
    dayShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};
const formatters = {
    "%d": (date) => {
        const day = date.getDate();
        return day < 10 ? "0" + day : day;
    },
    "%j": (date) => date.getDate(),
    "%l": (date) => {
        return locales.dayFull[date.getDay()];
    },
    "%D": (date) => {
        return locales.dayShort[date.getDay()];
    },
    "%m": (date) => {
        const month = date.getMonth() + 1;
        return month < 10 ? "0" + month : month;
    },
    "%n": (date) => date.getMonth() + 1,
    "%M": (date) => locales.monthShort[date.getMonth()],
    "%F": (date) => locales.monthFull[date.getMonth()],
    "%y": (date) => date.getFullYear().toString().slice(2),
    "%Y": (date) => date.getFullYear(),
};
const setFormatters = {
    "%d": (date, value) => {
        const check = /(^([0-9][0-9])$)/i.test(value);
        check ? date.setDate(Number(value)) : date.setDate(Number(1));
    },
    "%j": (date, value) => {
        const check = /(^([0-9]?[0-9])$)/i.test(value);
        check ? date.setDate(Number(value)) : date.setDate(Number(1));
    },
    "%m": (date, value) => {
        const check = /(^([0-9][0-9])$)/i.test(value);
        check ? date.setMonth(Number(value) - 1) : date.setMonth(Number(0));
        if (check && date.getMonth() !== Number(value) - 1)
            date.setMonth(Number(value) - 1);
    },
    "%n": (date, value) => {
        const check = /(^([0-9]?[0-9])$)/i.test(value);
        check ? date.setMonth(Number(value) - 1) : date.setMonth(Number(0));
        if (check && date.getMonth() !== Number(value) - 1)
            date.setMonth(Number(value) - 1);
    },
    "%M": (date, value) => {
        const index = findIndex(locales.monthShort, v => v === value);
        index === -1 ? date.setMonth(0) : date.setMonth(index);
        if (index !== -1 && date.getMonth() !== index)
            date.setMonth(index);
    },
    "%F": (date, value) => {
        const index = findIndex(locales.monthFull, v => v === value);
        index === -1 ? date.setMonth(0) : date.setMonth(index);
        if (index !== -1 && date.getMonth() !== index)
            date.setMonth(index);
    },
    "%y": (date, value) => {
        const check = /(^([0-9][0-9])$)/i.test(value);
        check
            ? date.setFullYear(Number("20" + value))
            : date.setFullYear(Number("2000"));
    },
    "%Y": (date, value) => {
        const check = /(^([0-9][0-9][0-9][0-9])$)/i.test(value);
        check
            ? date.setFullYear(Number(value))
            : date.setFullYear(Number("2000"));
    },
};
var TokenType;
(function (TokenType) {
    TokenType[TokenType["separator"] = 0] = "separator";
    TokenType[TokenType["datePart"] = 1] = "datePart";
})(TokenType || (TokenType = {}));
function tokenizeFormat(format) {
    const tokens = [];
    let currentSeparator = "";
    for (let i = 0; i < format.length; i++) {
        if (format[i] === "%") {
            if (currentSeparator.length > 0) {
                tokens.push({
                    type: TokenType.separator,
                    value: currentSeparator,
                });
                currentSeparator = "";
            }
            tokens.push({
                type: TokenType.datePart,
                value: format[i] + format[i + 1],
            });
            i++;
        }
        else {
            currentSeparator += format[i];
        }
    }
    if (currentSeparator.length > 0) {
        tokens.push({
            type: TokenType.separator,
            value: currentSeparator,
        });
    }
    return tokens;
}
export function dateToString(date, format, locale) {
    if (locale)
        locales = locale;
    return tokenizeFormat(format).reduce((res, token) => {
        if (token.type === TokenType.separator) {
            return res + token.value;
        }
        else {
            if (!formatters[token.value]) {
                return res;
            }
            return res + formatters[token.value](date);
        }
    }, "");
}
export function stringToDate(string, format, validate = false, locale) {
    if (typeof string !== "string") {
        return;
    }
    if (locale)
        locales = locale;
    const tokens = tokenizeFormat(format);
    const dateParts = [];
    let index = 0;
    let formatter = null;
    for (const token of tokens) {
        if (token.type === TokenType.separator) {
            const sepratorIndex = string.indexOf(token.value, index);
            if (sepratorIndex === -1) {
                if (validate) {
                    return false;
                }
                if (isDate(string)) {
                    return new Date(string);
                }
                throw new Error("Incorrect Date or date format");
            }
            if (formatter) {
                dateParts.push({
                    formatter,
                    value: string.slice(index, sepratorIndex),
                });
                formatter = null;
            }
            index = sepratorIndex + token.value.length;
        }
        else if (token.type === TokenType.datePart) {
            formatter = token.value;
        }
    }
    if (formatter === "%A" || formatter === "%a") {
        dateParts.unshift({
            formatter,
            value: string.slice(index),
        });
    }
    else if (formatter) {
        dateParts.push({
            formatter,
            value: string.slice(index),
        });
    }
    dateParts.reverse();
    let dateFormat;
    for (const datePart of dateParts) {
        if (datePart.formatter === "%A" || datePart.formatter === "%a") {
            dateFormat = datePart.value;
        }
    }
    const date = new Date(0);
    for (const datePart of dateParts) {
        if (setFormatters[datePart.formatter]) {
            setFormatters[datePart.formatter](date, datePart.value, dateFormat);
        }
    }
    return date;
}
export function isDate(value) {
    if (typeof value === "string") {
        const dateFormat = new RegExp("^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?(Z)?$");
        return dateFormat.test(value);
    }
    return value instanceof Date;
}
export function isLastDate(value) {
    const now = new Date();
    const diffNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffValue = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    return diffNow.getTime() <= diffValue.getTime();
}
//# sourceMappingURL=date.js.map